// FBC Lab v10 — Consultation Intent Router
// Deterministic intent detection before stage-based routing.
// Direct user intent is answered before sales discovery.

import { isRecapNegated, isWeekOnePlanAsk, visibleUserTextForIntent } from './utils/consulting-move.js'

export type ConsultationIntent =
  | 'identity_question'
  | 'research_question'
  | 'visual_question'
  | 'screen_question'
  | 'webcam_question'
  | 'location_weather_question'
  | 'file_question'
  | 'voice_or_audio_question'
  | 'debug_or_failure_report'
  | 'report_request'
  | 'booking_request'
  | 'conversation_recall'
  | 'business_discovery_signal'
  | 'workshop_signal'
  | 'general_question'

export interface IntentDetectionResult {
  intent: ConsultationIntent
  confidence: number
  matchedKeywords: string[]
}

const ACKNOWLEDGMENT_PATTERNS = [
  /^\s*(ok(?:ay)?|thanks?|thank you|got it|cool|great|perfect|sounds good|appreciate it|cheers)\s*[!.]*\s*$/i,
  /^\s*(ok(?:ay)?|thanks?|thank you)[,.]?\s*(got it|sounds good|perfect|great)\s*[!.]*\s*$/i,
]

const IDENTITY_PATTERNS = [
  /\b(what did you find|what else did you find|what do you know about me|who am i|my profile|research about me|about me)\b/,
  /\b(?:what do you know|tell me)\s+about\s+my\s+(?:company|role)\b/,
  /\b(?:did you find|have you found)\s+(?:any\s+)?(?:other\s+)?(?:relevant\s+)?information\b/,
  /\b(?:tell me more|more)\s+about\s+(?:the\s+)?(?:company|person|profile|research|brief)\s+you\s+(?:found|looked up|researched)\b/,
]

const RESEARCH_PATTERNS = [
  /\b(source|sources|confidence|grounding|research|lookup|looked up|citation|citations|where did you get that|how do you know)\b/,
]

const WEATHER_PATTERNS = [
  /\b(weather|temperature|forecast|rain|sunny|cold|hot|snow|wind|humidity|celsius|fahrenheit)\b/,
]

const SCREEN_PATTERNS = [
  /\b(screen|screenshare|screen share|logs|vercel|dashboard|deployment|console|terminal|browser|page|error log|build log|server log)\b/,
]

const WEBCAM_PATTERNS = [
  // Keep literals in sync with LIVE_WEBCAM_INTENT_REGEX (no room/face/environment).
  /\b(webcam|camera|see me|look at me)\b/,
]

const VISUAL_PATTERNS = [
  /\b(can you see|what do you see|look at|visible|describe what you see|do you see this|am i visible)\b/,
  /\b(what did you see|did you see|what have you seen|tell me what you saw|describe what you saw|what you saw earlier|and what did you see)\b/,
  /\b(summarize what you saw|what was on|what was on my screen|what was on the webcam|what did you see with your vision)\b/,
]

const FILE_PATTERNS = [
  /\b(file|pdf|document|attachment|upload|spreadsheet|csv|data file|report file)\b/,
]

const VOICE_PATTERNS = [
  /\b(voice|audio|hear me|listen|microphone|sound|speaker|call me)\b/,
]

const DEBUG_PATTERNS = [
  /\b(failed|broken|wrong|hallucinated|repeating|doesn't work|doesnt work|dont work|lost it|bug|you are wrong|you keep|stop repeating|why did you say that)\b/,
]

const REPORT_PATTERNS = [
  /\b(generate|create|send|download|export|save|email|mail)\b.{0,40}\b(report|pdf|transcript|conversation summary|session summary|brief)\b/,
  /\b(give me|show me|get me|pull up|let me see|send me|email me|mail me).{0,24}\b(report|pdf|transcript|summary|brief)\b/,
  /\b(?:want|need)\b.{0,24}\b(?:the |a |an )?(?:pdf|report|brief|discovery brief)\b/,
  /\bAI insights report\b/i,
  /\bdiscovery (?:report|brief)\b/i,
  /\b(export|save|download)\s+(the|this|my)\s+(conversation|session|chat|transcript)\b/,
  /\b(generer|lag|send|last ned)\b.{0,40}\b(rapport|brief|pdf)\b/,
  /\b(send meg|last ned|e-?post(?: meg)?).{0,24}\b(rapporten|briefen|pdf|rapport|brief)\b/,
  /\bkan du (?:sende|lage|generere).{0,24}\b(rapporten|briefen|rapport|brief|pdf)\b/,
  // #316 retest (sess_007efc6c): «Kan jeg få briefen nå?» was not a request, so no auto brief
  // and no short brief; the agent refused and no button showed.
  /\b(?:kan|kunne)\s+(?:jeg|vi)\s+få\s.{0,24}\b(?:rapport|brief|pdf)/,
  // v451: nb STT heard «Kan jeg få briefen nå?» as «…brevet nå?». Only in the «få» ask form.
  /\b(?:kan|kunne)\s+(?:jeg|vi)\s+få\s+brevet\b/,
  /\bfår\s+(?:jeg|vi)\s+brevet\b/,
  /\bfår\s+(?:jeg|vi)\s.{0,24}\b(?:rapport|brief|pdf)/,
  /\b(?:jeg|vi)\s+vil\s+(?:gjerne\s+)?ha\s.{0,24}\b(?:rapport|brief|pdf)/,
  /\b(?:can|could|may)\s+(?:i|we)\s+(?:get|have)\b.{0,24}\b(?:report|pdf|brief)\b/,
]

/** Typo-tolerant recap/PDF combos route to discovery report, not file upload. */
const REPORT_PDF_INTENT_PATTERNS = [
  // N33: the brief button label typed as a chat line, and its English form.
  /\blag\b.{0,30}\bmed\s+(?:den\s+|all\s+)?(?:informasjonen|det)\s+(?:vi|du|dere)\s+har\b/i,
  /\b(?:make|generate|create|write|build)\b.{0,30}\b(?:with|from)\s+(?:the information|what)\s+(?:we|you)\s+(?:have|know)\b/i,
  /\b(?:summari[sz]e|summrize|summeriz|sum up|recap|oppsummer|summary|everything|all).{0,80}\b(?:pdf|brief|rapport)\b/i,
  /\b(?:pdf|brief|rapport)\b.{0,80}\b(?:summari[sz]e|summrize|summeriz|sum up|recap|summary|everything|all|conversation|session|alt|alt det|samtalen)\b/i,
]

function isReportPdfIntent(text: string): boolean {
  const lower = text.toLowerCase()
  if (REPORT_PDF_INTENT_PATTERNS.some(p => p.test(lower))) return true
  if (!/\bpdf\b/.test(lower)) return false
  const hasRecap =
    /\b(?:summari[sz]e|summeriz|summ?rize|sum up|recap|summary|everything|all)\b/.test(lower)
  const hasExportVerb = /\b(?:generate|create|send|download|export|save|give me|show me)\b/.test(
    lower
  )
  return hasRecap || hasExportVerb
}

const BOOKING_PATTERNS = [
  /\b(book|booking|call|meeting|schedule|calendar|appointment|consultation call|talk to farzad|speak with farzad|neste steg|neste skritt|pris|prisen|koster|kostnad|bestill|avtal|få tilbud|vil du|høres bra ut|hva nå)\b/,
]

const CONVERSATION_RECALL_PATTERNS = [
  /\b(what have we been talking about|what did we discuss|what were we talking about|recap|summary so far|what have we covered|remind me what we discussed)\b/,
  /\b(?:oppsummer|oppsumer|oppsumér)(?:\s+(?:alt|alt det|det|everything|session|samtalen|økten))?\b/i,
  /\b(?:var det alt|det var alt)\b/i,
  /\b(summari[sz]e|summrize|summeriz|sum up|recap|full summary|complete summary|session summary|conversation summary)\b.{0,140}\b(everything|all|conversation|thread|talked about|discussed|covered|found out|found|saw|webcam|camera|screen|screenshare|screen share|logs?|dashboard|issue|issues|vercel|today)\b/,
  /\b(give me|show me|get me|pull up|let me see).{0,20}\b(full summary|complete summary|conversation summary|session summary)\b/,
  /\b(what did i ask|what questions did i ask|what did you find out about me and what did you see)\b/,
  /\b(?:do you (?:have |still )?(?:memory|remember)|have you remembered|memory of what we)\b/,
  /\bwhat we have (?:discussed|talked about|covered|tested)\b/,
  /\bwhat did we (?:test|talk about|cover|go over)\b/,
  /\b(?:summari[sz]e|summrize|summeriz|sum up|recap)\b.{0,80}\b(?:today|this (?:chat|session|conversation))\b/,
]

/** Meta checks about voice/text routing — not questions about voice capability. */
const SESSION_CHANNEL_CHECK_PATTERNS = [
  /\b(?:quick check|can i still send|after voice|still send text|send text).{0,50}\b(?:voice|text|send|message)\b/i,
]

const WORKSHOP_PATTERNS = [
  /\b(workshop|workshopprosjekt|training|teach my team|session|course|learn ai|bootcamp|seminar|team training|kurs|opplæring|trening|lære)\b/,
]

const BUSINESS_PATTERNS = [
  /\b(ai strategy|automation|workflow|business|revenue|cost|pain|problem|bottleneck|team|crm|data source|integration|lead generation|sales funnel|customer support|efficiency|scale|growth)\b/,
]

function testPatterns(text: string, patterns: RegExp[]): { matches: boolean; keywords: string[] } {
  const keywords: string[] = []
  let matches = false
  for (const pattern of patterns) {
    const found = text.match(pattern)
    if (found) {
      matches = true
      keywords.push(found[0])
    }
  }
  return { matches, keywords }
}

/** Recap / “what did we discuss” — classified as conversation_recall; Live owns the turn. */
export function isConversationRecallQuestion(input: string): boolean {
  if (!input?.trim()) return false
  return detectConsultationIntent(input).intent === 'conversation_recall'
}

/** Visitor asked to generate or send the discovery brief — not a recap. */
export function isDiscoveryReportRequest(input: string): boolean {
  if (!input?.trim()) return false
  return detectConsultationIntent(input).intent === 'report_request'
}

const BRIEF_OFFER_TOPIC =
  /\b(?:oppsummering\w*|rapport\w*|brief\w*|pdf(?:-en)?|summary|summaries|report|brief)\b/i
const BRIEF_OFFER_ASK =
  /\?|\b(?:vil du|skal jeg|ønsker du|vil dere|want me|shall i|should i|would you like|do you want)\b/i
const YES_TO_OFFER =
  /^(?:ja|jepp|jo|ja takk|ja gjerne|gjerne|ok|okay|greit|absolutt|yes|yeah|yep|yes please|sure|absolutely|please do)$/i

/** The assistant line offered to write the brief/summary/report (1018 item 4). */
export function isBriefOfferLine(text: string | undefined): boolean {
  const line = text?.trim()
  if (!line) return false
  return BRIEF_OFFER_TOPIC.test(line) && BRIEF_OFFER_ASK.test(line)
}

/**
 * A short yes to the agent's own brief offer is a brief ask (v449: «Vil du at jeg skal
 * generere en oppsummeringsrapport nå?» → «Ja.» was refused as DOSSIER_INCOMPLETE).
 */
export function isYesToBriefOffer(userText: string | undefined, lastAssistantText: string | undefined): boolean {
  const bare = (userText || '')
    .toLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return Boolean(bare) && YES_TO_OFFER.test(bare) && isBriefOfferLine(lastAssistantText)
}

export function isExplicitBookingAction(input: string): boolean {
  const text = input.toLowerCase().trim()
  if (!text) return false
  if (/\b(?:before|prior to|ahead of)\s+(?:booking|scheduling|the call|a call)\b/.test(text)) {
    return false
  }
  return (
    /\b(?:book|schedule|arrange|set up|open)\b.{0,40}\b(?:call|meeting|calendar|consultation|time|slot)\b/.test(text) ||
    /\b(?:book|schedule)\s+(?:with|farzad|directly|now|today)\b/.test(text) ||
    /\b(?:open|show|send|give me|get me)\b.{0,30}\b(?:calendar|booking link|cal\.com)\b/.test(text) ||
    /\b(?:book a time with farzad|open calendar|talk to farzad)\b/.test(text) ||
    /\b(?:booke|bestill(?:e|er)?|avtal(?:e)?)\b.{0,48}\b(?:samtale|møte|tid|kalender)\b/.test(text) ||
    /\b(?:gi meg|send|vis|åpne)\b.{0,30}\b(?:bestillingslenke(?:n)?|kalender(?:en)?|booking)\b/.test(text)
  )
}

export function detectConsultationIntent(input: string): IntentDetectionResult {
  const text = visibleUserTextForIntent(input).toLowerCase().trim()

  if (isReportPdfIntent(text)) {
    return {
      intent: 'report_request',
      confidence: 0.92,
      matchedKeywords: ['pdf', 'summary'],
    }
  }

  // "no recap, pick the first bottleneck..." is a consulting ask, not a recap dump.
  if (isRecapNegated(text) || (isWeekOnePlanAsk(text) && !/\b(?:what have we|what did we discuss|summary so far|oppsummer)\b/i.test(text))) {
    const business = testPatterns(text, BUSINESS_PATTERNS)
    return {
      intent: business.matches || isWeekOnePlanAsk(text) ? 'business_discovery_signal' : 'general_question',
      confidence: 0.9,
      matchedKeywords: business.keywords.length > 0 ? business.keywords : ['week-one-plan'],
    }
  }

  // Order matters: more specific intents first
  const tests: Array<{
    intent: ConsultationIntent
    patterns: RegExp[]
    priority: number
  }> = [
    { intent: 'debug_or_failure_report', patterns: DEBUG_PATTERNS, priority: 100 },
    { intent: 'conversation_recall', patterns: CONVERSATION_RECALL_PATTERNS, priority: 96 },
    { intent: 'general_question', patterns: ACKNOWLEDGMENT_PATTERNS, priority: 94 },
    { intent: 'identity_question', patterns: IDENTITY_PATTERNS, priority: 95 },
    { intent: 'research_question', patterns: RESEARCH_PATTERNS, priority: 90 },
    { intent: 'report_request', patterns: REPORT_PATTERNS, priority: 85 },
    { intent: 'booking_request', patterns: BOOKING_PATTERNS, priority: 80 },
    { intent: 'workshop_signal', patterns: WORKSHOP_PATTERNS, priority: 75 },
    { intent: 'business_discovery_signal', patterns: BUSINESS_PATTERNS, priority: 70 },
    { intent: 'location_weather_question', patterns: WEATHER_PATTERNS, priority: 65 },
    { intent: 'screen_question', patterns: SCREEN_PATTERNS, priority: 60 },
    { intent: 'webcam_question', patterns: WEBCAM_PATTERNS, priority: 60 },
    { intent: 'visual_question', patterns: VISUAL_PATTERNS, priority: 55 },
    { intent: 'file_question', patterns: FILE_PATTERNS, priority: 50 },
    { intent: 'general_question', patterns: SESSION_CHANNEL_CHECK_PATTERNS, priority: 46 },
    { intent: 'voice_or_audio_question', patterns: VOICE_PATTERNS, priority: 45 },
  ]

  for (const test of tests) {
    const result = testPatterns(text, test.patterns)
    if (result.matches) {
      return {
        intent: test.intent,
        confidence: Math.min(0.5 + result.keywords.length * 0.15, 0.95),
        matchedKeywords: result.keywords,
      }
    }
  }

  return {
    intent: 'general_question',
    confidence: 0.55,
    matchedKeywords: [],
  }
}

export function isCapabilityIntent(intent: ConsultationIntent): boolean {
  return (
    intent !== 'business_discovery_signal' &&
    intent !== 'workshop_signal' &&
    intent !== 'booking_request' &&
    intent !== 'general_question'
  )
}

export function isSalesIntent(intent: ConsultationIntent): boolean {
  return (
    intent === 'business_discovery_signal' ||
    intent === 'workshop_signal' ||
    intent === 'booking_request'
  )
}

export function getIntentDescription(intent: ConsultationIntent): string {
  const descriptions: Record<ConsultationIntent, string> = {
    identity_question: 'User asks about their identity/research profile',
    research_question: 'User asks about research sources/methodology',
    visual_question: 'User asks about visual perception (unspecified source)',
    screen_question: 'User asks about screen share content',
    webcam_question: 'User asks about webcam content',
    location_weather_question: 'User asks about weather or location',
    file_question: 'User asks about uploaded files/documents',
    voice_or_audio_question: 'User asks about voice/audio',
    debug_or_failure_report: 'User reports a bug, failure, or hallucination',
    report_request: 'User requests a report/PDF/transcript',
    booking_request: 'User wants to book a call/meeting',
    conversation_recall: 'User asks for a recap of the conversation so far',
    business_discovery_signal: 'User expresses business pain/AI need',
    workshop_signal: 'User expresses workshop/training interest',
    general_question: 'General question without specific intent',
  }
  return descriptions[intent] ?? 'Unknown intent'
}
